import React from 'react';
import { X as CloseIcon, StarIcon, CheckIcon, MapPinIcon } from 'lucide-react';
import Button from '../common/Button';
import { Hotel } from '../../utils/types';
interface HotelDetailsDrawerProps {
  hotel: Hotel | null;
  isOpen: boolean;
  onClose: () => void;
  onSelectRoom: (roomType: string, price: number) => void;
}
const HotelDetailsDrawer: React.FC<HotelDetailsDrawerProps> = ({
  hotel,
  isOpen,
  onClose,
  onSelectRoom
}) => {
  if (!isOpen || !hotel) return null;
  // Mock room types data
  const roomTypes = [{
    id: 'r1',
    name: 'Standard Room',
    description: 'Comfortable room with basic amenities',
    price: hotel.pricePerNight,
    beds: '1 Queen Bed',
    maxOccupancy: 2,
    size: '25 m²',
    amenities: ['Free Wi-Fi', 'TV', 'Air conditioning', 'Private bathroom'],
    image: hotel.images[0],
    refundable: true
  }, {
    id: 'r2',
    name: 'Deluxe Room',
    description: 'Spacious room with premium amenities and city view',
    price: hotel.pricePerNight * 1.3,
    beds: '1 King Bed',
    maxOccupancy: 2,
    size: '35 m²',
    amenities: ['Free Wi-Fi', 'Smart TV', 'Air conditioning', 'Private bathroom', 'City view', 'Mini-bar'],
    image: hotel.images.length > 1 ? hotel.images[1] : hotel.images[0],
    refundable: true
  }, {
    id: 'r3',
    name: 'Family Suite',
    description: 'Large suite ideal for families with separate living area',
    price: hotel.pricePerNight * 1.8,
    beds: '2 Queen Beds',
    maxOccupancy: 4,
    size: '45 m²',
    amenities: ['Free Wi-Fi', 'Smart TV', 'Air conditioning', 'Private bathroom', 'Living area', 'Mini-bar', 'Coffee maker'],
    image: hotel.images.length > 2 ? hotel.images[2] : hotel.images[0],
    refundable: true
  }];
  // Generate star rating display
  const renderStars = (count: number) => {
    return Array(5).fill(0).map((_, i) => <StarIcon key={i} className={`h-4 w-4 ${i < count ? 'text-yellow-400' : 'text-gray-300'}`} fill={i < count ? 'currentColor' : 'none'} data-id="element-372" />);
  };
  return <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-373">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} data-id="element-374"></div>
      <div className="fixed inset-y-0 right-0 max-w-full flex" data-id="element-375">
        <div className="relative w-full md:w-[600px] bg-white shadow-xl" data-id="element-376">
          <div className="h-full flex flex-col overflow-y-auto" data-id="element-377">
            {/* Header with close button */}
            <div className="px-4 py-6 bg-white border-b border-gray-200 sm:px-6" data-id="element-378">
              <div className="flex items-start justify-between" data-id="element-379">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-380">
                  Hotel Details
                </h2>
                <button onClick={onClose} className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-381">
                  <CloseIcon className="h-6 w-6" data-id="element-382" />
                </button>
              </div>
            </div>
            {/* Hotel details content */}
            <div className="flex-1 overflow-y-auto p-6" data-id="element-383">
              <div className="space-y-6" data-id="element-384">
                {/* Hotel images carousel (simplified) */}
                <div className="relative h-64 overflow-hidden rounded-lg" data-id="element-385">
                  <img src={hotel.thumbnail} alt={hotel.name} className="w-full h-full object-cover" data-id="element-386" />
                </div>
                {/* Hotel name and rating */}
                <div data-id="element-387">
                  <h1 className="text-2xl font-bold text-gray-900" data-id="element-388">
                    {hotel.name}
                  </h1>
                  <div className="flex items-center mt-2" data-id="element-389">
                    <div className="flex mr-2" data-id="element-390">{renderStars(hotel.stars)}</div>
                    <span className="text-sm text-gray-600" data-id="element-391">
                      {hotel.rating} ({hotel.reviewCount} reviews)
                    </span>
                  </div>
                  <div className="flex items-center mt-2 text-gray-600" data-id="element-392">
                    <MapPinIcon className="h-4 w-4 mr-1" data-id="element-393" />
                    <span data-id="element-394">
                      {hotel.address}, {hotel.city}, {hotel.country}
                    </span>
                  </div>
                </div>
                {/* Amenities */}
                <div data-id="element-395">
                  <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-396">
                    Amenities
                  </h2>
                  <div className="grid grid-cols-2 gap-2" data-id="element-397">
                    {hotel.amenities.map((amenity, index) => <div key={index} className="flex items-center text-sm text-gray-600" data-id="element-398">
                        <CheckIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-399" />
                        {amenity}
                      </div>)}
                  </div>
                </div>
                {/* Room types */}
                <div data-id="element-400">
                  <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-401">
                    Available Room Types
                  </h2>
                  <div className="space-y-4" data-id="element-402">
                    {roomTypes.map(room => <div key={room.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300" data-id="element-403">
                        <div className="flex flex-col md:flex-row" data-id="element-404">
                          <div className="md:w-1/3 h-24 md:h-auto mb-4 md:mb-0" data-id="element-405">
                            <img src={room.image} alt={room.name} className="w-full h-full object-cover rounded-md" data-id="element-406" />
                          </div>
                          <div className="md:w-2/3 md:pl-4" data-id="element-407">
                            <h3 className="font-medium text-gray-900" data-id="element-408">
                              {room.name}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1" data-id="element-409">
                              {room.description}
                            </p>
                            <div className="flex items-center mt-2 text-sm text-gray-600" data-id="element-410">
                              <span className="mr-3" data-id="element-411">{room.beds}</span>
                              <span className="mr-3" data-id="element-412">
                                Max {room.maxOccupancy} guests
                              </span>
                              <span data-id="element-413">{room.size}</span>
                            </div>
                            <div className="mt-3" data-id="element-414">
                              <span className="text-lg font-bold text-blue-600" data-id="element-415">
                                ${Math.round(room.price)}
                              </span>
                              <span className="text-gray-500 text-sm ml-1" data-id="element-416">
                                per night
                              </span>
                            </div>
                            <div className="mt-3" data-id="element-417">
                              <Button variant="primary" size="sm" onClick={() => onSelectRoom(room.name, room.price)} data-id="element-418">
                                Select This Room
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
                {/* Policies */}
                <div data-id="element-419">
                  <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-420">
                    Hotel Policies
                  </h2>
                  <div className="space-y-2 text-sm text-gray-600" data-id="element-421">
                    <p data-id="element-422">Check-in: After 3:00 PM</p>
                    <p data-id="element-423">Check-out: Before 12:00 PM</p>
                    <p data-id="element-424">{hotel.cancellationPolicy}</p>
                    <p data-id="element-425">{hotel.mealPlan}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default HotelDetailsDrawer;